import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, useDragControls, PanInfo } from 'framer-motion';
import { useKeyboardInset } from '../../hooks/useKeyboardInset';

type SnapPoint = 'collapsed' | 'half' | 'full';

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  title?: string;
  description?: string;
  initialSnap?: SnapPoint;
}

const SNAP_POINTS: Record<SnapPoint, number> = {
  collapsed: 0.4,
  half: 0.6,
  full: 0.9,
};

const DISMISS_THRESHOLD = 0.25;

// Sheet box height as a fraction of the viewport. It exceeds the tallest snap
// point on purpose, so the sheet still covers the screen edge when dragged up.
const SHEET_HEIGHT_RATIO = 0.95;

// Breathing room between the content's bottom edge and the top of the keyboard.
const KEYBOARD_GAP_PX = 8;

export function BottomSheet({
  open,
  onOpenChange,
  children,
  title,
  description,
  initialSnap = 'collapsed',
}: BottomSheetProps) {
  const [currentSnap, setCurrentSnap] = useState<SnapPoint>(initialSnap);
  const dragControls = useDragControls();
  const sheetRef = useRef<HTMLDivElement>(null);
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800
  );

  // Synchronously reset snap when dialog opens (getDerivedStateFromProps pattern)
  const prevOpen = useRef(false);
  if (open && !prevOpen.current && currentSnap !== initialSnap) {
    setCurrentSnap(initialSnap);
  }
  prevOpen.current = open;

  // Derive targetY synchronously — no useEffect delay so animate !== initial on mount
  const targetY = open
    ? windowHeight * (1 - SNAP_POINTS[currentSnap])
    : windowHeight;

  // Track viewport height for snap math — but NOT while the sheet is open. The
  // mobile keyboard opening shrinks window.innerHeight, which would otherwise
  // resize the sheet mid-interaction and make it jump/squish. Sync once on open
  // (the keyboard is still closed at that instant), then ignore resizes until it
  // closes again; while closed we keep tracking so orientation changes apply.
  useEffect(() => {
    if (open) {
      setWindowHeight(window.innerHeight);
      return;
    }
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open]);

  // Lifts the sheet clear of the on-screen keyboard; 0 when no keyboard is up.
  const keyboardInset = useKeyboardInset(open);

  const snapTo = useCallback(
    (snap: SnapPoint) => {
      setCurrentSnap(snap);
    },
    []
  );

  const handleDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const currentY = windowHeight * (1 - SNAP_POINTS[currentSnap]);
      const newY = currentY + info.offset.y;
      const velocity = info.velocity.y;

      const sheetHeight = windowHeight - newY;
      const sheetPercent = sheetHeight / windowHeight;

      if (sheetPercent < DISMISS_THRESHOLD || velocity > 500) {
        onOpenChange(false);
        return;
      }

      let targetSnap: SnapPoint = 'collapsed';
      const snapValues = Object.entries(SNAP_POINTS) as [SnapPoint, number][];

      if (velocity < -200) {
        const currentIdx = snapValues.findIndex(([s]) => s === currentSnap);
        const nextIdx = Math.min(currentIdx + 1, snapValues.length - 1);
        const nextSnap = snapValues[nextIdx];
        if (nextSnap) targetSnap = nextSnap[0];
      } else if (velocity > 200) {
        const currentIdx = snapValues.findIndex(([s]) => s === currentSnap);
        const nextIdx = Math.max(currentIdx - 1, 0);
        const prevSnap = snapValues[nextIdx];
        if (prevSnap) targetSnap = prevSnap[0];
      } else {
        let minDistance = Infinity;
        for (const [snap, percent] of snapValues) {
          const distance = Math.abs(sheetPercent - percent);
          if (distance < minDistance) {
            minDistance = distance;
            targetSnap = snap;
          }
        }
      }

      snapTo(targetSnap);
    },
    [currentSnap, onOpenChange, snapTo, windowHeight]
  );

  // Element that had focus when the sheet opened, so we can hand focus back on
  // close. Radix cannot do this for us here — see handleCloseAutoFocus.
  const openerRef = useRef<HTMLElement | null>(null);

  // Latest `open` readable from Radix's focus callbacks (see handleCloseAutoFocus).
  const isOpenRef = useRef(open);
  isOpenRef.current = open;

  // Radix's default open-autofocus lands on the first focusable child, which on
  // most sheets is a text input — that pops the mobile keyboard the instant the
  // sheet opens and shrinks innerHeight mid-animation. But simply preventing the
  // default left focus on the trigger button, which sits inside the background
  // subtree Radix marks aria-hidden while a modal dialog is open. The browser
  // then refuses to apply it ("Blocked aria-hidden on an element because its
  // descendant retained focus") and screen-reader users are stranded outside the
  // sheet. Move focus to the sheet container instead: no keyboard, and focus
  // lands inside the dialog so the focus trap has something to hold. Radix puts
  // tabIndex={-1} on Content, so the container is focusable.
  const handleOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    // Radix fires this before moving focus, so activeElement is still the opener.
    const active = document.activeElement;
    // Belt-and-braces: only record an opener from outside the sheet, so a repeat
    // fire could never record the sheet as its own opener.
    if (
      active instanceof HTMLElement &&
      active !== document.body &&
      !sheetRef.current?.contains(active)
    ) {
      openerRef.current = active;
    }
    sheetRef.current?.focus({ preventScroll: true });
  }, []);

  // Radix's modal Dialog does not restore focus to whatever had it before — it
  // restores to `Dialog.Trigger` specifically. DialogContentModal's own
  // onCloseAutoFocus calls `event.preventDefault(); context.triggerRef.current
  // ?.focus()`, so the focus scope's generic restore never runs. Every sheet in
  // this app is a controlled `Dialog.Root` opened by an ordinary button, so no
  // `Dialog.Trigger` is ever rendered, `triggerRef` is null, and that focus()
  // is a no-op — focus stays on <body> where it landed when the sheet unmounted.
  //
  // preventDefault() here is what makes this work: Radix composes our handler
  // ahead of its own with composeEventHandlers, which skips its handler once the
  // event is defaulted. So we suppress the trigger-only restore and do the real
  // one. (If a sheet is ever given a real Dialog.Trigger, delete all of this.)
  const handleCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    // The focus scope also dispatches this event whenever its container element
    // changes, not only on close. That was not observed for these sheets, but if
    // it happens, keep focus in the open sheet instead of yanking it to the opener.
    if (isOpenRef.current) {
      sheetRef.current?.focus({ preventScroll: true });
      return;
    }
    const opener = openerRef.current;
    openerRef.current = null;
    if (opener && document.body.contains(opener)) {
      opener.focus({ preventScroll: true });
    }
  }, []);

  // The sheet already hangs this far past the bottom of the viewport, so the
  // keyboard eats that slack before it starts covering content. The spacer below
  // is exactly `targetY` tall and cancels the sheet's own offset, which is why
  // content always ends this far up regardless of which snap point is active.
  const bottomSlack = windowHeight * (1 - SHEET_HEIGHT_RATIO);
  const keyboardPadding =
    keyboardInset > 0 ? Math.max(0, keyboardInset - bottomSlack + KEYBOARD_GAP_PX) : 0;

  const backdropOpacity = currentSnap === 'full' ? 0.7 : currentSnap === 'half' ? 0.5 : 0.3;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay asChild>
          <motion.div
            className="fixed inset-0 z-40 bg-black"
            initial={{ opacity: 0 }}
            animate={{ opacity: backdropOpacity }}
            exit={{ opacity: 0 }}
            onClick={() => onOpenChange(false)}
            aria-hidden="true"
          />
        </Dialog.Overlay>
        <Dialog.Content
          asChild
          onOpenAutoFocus={handleOpenAutoFocus}
          onCloseAutoFocus={handleCloseAutoFocus}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <motion.div
            ref={sheetRef}
            className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl bg-background shadow-elevated focus:outline-none"
            style={{ height: windowHeight * SHEET_HEIGHT_RATIO, paddingBottom: keyboardPadding }}
            initial={{ y: windowHeight }}
            animate={{ y: targetY }}
            transition={{ type: 'tween', duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            exit={{ y: windowHeight }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: windowHeight * 0.05, bottom: windowHeight }}
            dragElastic={0.1}
            onDragEnd={handleDragEnd}
          >
            {/* Drag handle area */}
            <div
              className="flex cursor-grab touch-none flex-col items-center px-4 pb-2 pt-3 active:cursor-grabbing"
              onPointerDown={(e) => dragControls.start(e)}
            >
              {/* Visual drag handle indicator */}
              <div
                className="h-1 w-10 rounded-full bg-gray-300"
                aria-hidden="true"
              />
            </div>

            {/* Header */}
            {(title || description) && (
              <div className="border-b border-gray-100 px-4 pb-3">
                {title && (
                  <Dialog.Title className="text-lg font-semibold text-text-on-light">
                    {title}
                  </Dialog.Title>
                )}
                {description && (
                  <Dialog.Description className="mt-1 text-sm text-gray-500">
                    {description}
                  </Dialog.Description>
                )}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 text-text-on-light">
              {children}
            </div>

            {/* Spacer: occupies the off-screen area below viewport so content stays visible */}
            <div style={{ minHeight: targetY, flexShrink: 0 }} aria-hidden="true" />
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
